import { createSlice } from '@reduxjs/toolkit';
import {
  fetchTeam,
  fetchTeams,
  fetchParentTeams,
  fetchUsersOnTeam,
  fetchUsersNotOnTeam,
  bulkActionTeamUsers,
  addTeam,
  editTeam,
  deleteTeam,
  downloadTeams,
  resetTeamAdded,
  fetchBgEventTasks,
  teamsImport,
} from '../actions';

const initialState = {
  team: {},
  teamsCount: 0,
  usersOnTeam: [],
  usersOnTeamCount: 0,
  usersNotOnTeam: [],
  usersNotOnTeamCount: 0,
  events: [],
  eventsCount: 0,
  teams: [],
  parentTeams: [],
  page: 1,
  teamAdded: {},
  teamEdited: {},
  importTeams: {},
  backgroundEvents: {},
  teamDeletedResult: '',
  teamsDownloadedResult: '',
  usersBulkEditActionResult: '',
  teamsSearchKey: '',
  isLoading: false,
  isFetchingTeams: true,
  isImportingTeams: false,
  isFetchingTeamUsers: false,
  isFetchingBgEvents: false,
  initiateFetchUsersOnTeam: false,
};

const teamsSlice = createSlice({
  name: 'team',
  initialState,
  reducers: {},

  extraReducers: (builder) => {
    builder.addCase(fetchTeam.pending, (state) => {
      state.isLoading = true;
      state.team = {};
    });

    builder.addCase(fetchTeam.fulfilled, (state, action) => {
      state.isLoading = false;
      state.team = action.payload.team;
    });

    builder.addCase(fetchTeam.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload.error;
    });

    builder.addCase(fetchTeams.pending, (state) => {
      state.isLoading = true;
      state.isFetchingTeams = true;
    });

    builder.addCase(fetchTeams.fulfilled, (state, action) => {
      const { page, items } = action.payload.paging;
      state.teams = action.payload.teams || [];
      state.page = page;
      state.teamsCount = items;
      state.isLoading = false;
      state.isFetchingTeams = false;
    });

    builder.addCase(fetchTeams.rejected, (state, action) => {
      state.error = action.payload.error;
      state.isLoading = false;
      state.isFetchingTeams = false;
    });

    builder.addCase(fetchParentTeams.pending, (state) => {
      state.isLoading = true;
      state.isFetchingTeams = true;
    });

    builder.addCase(fetchParentTeams.fulfilled, (state, action) => {
      const { page, items } = action.payload.paging;
      state.parentTeams = action.payload.teams || [];
      state.page = page;
      state.teamsCount = items;
      state.isLoading = false;
      state.isFetchingTeams = false;
    });

    builder.addCase(fetchParentTeams.rejected, (state, action) => {
      state.error = action.payload.error;
      state.isLoading = false;
      state.isFetchingTeams = false;
    });

    builder.addCase(addTeam.pending, (state) => {
      state.isLoading = true;
    });

    builder.addCase(addTeam.fulfilled, (state, action) => {
      state.teamAdded = action.payload.teamInstance;
      state.isLoading = false;
    });

    builder.addCase(addTeam.rejected, (state, action) => {
      state.error = action.payload.error;
      state.isLoading = false;
    });

    builder.addCase(editTeam.pending, (state) => {
      state.editingTeam = true;
    });

    builder.addCase(editTeam.fulfilled, (state, action) => {
      state.teamEdited = action.payload.teamInstance;
      state.editingTeam = false;

      const { teamName } = action.meta.arg;
      state.team.name = teamName;
    });

    builder.addCase(editTeam.rejected, (state, action) => {
      state.error = action.payload.error;
      state.editingTeam = false;
    });

    builder.addCase(deleteTeam.pending, (state) => {
      state.teamDeletedResult = '';
      state.deletingTeam = true;
    });

    builder.addCase(deleteTeam.fulfilled, (state, action) => {
      state.teamDeletedResult = action.payload.apiResult.status;
      state.deletingTeam = false;

      const { teamId } = action.meta.arg;
      state.teams = state.teams.filter((team) => team.id !== teamId);
      state.teamsCount--;
    });

    builder.addCase(deleteTeam.rejected, (state, action) => {
      state.teamDeletedResult = action.payload.apiResult.status;
      state.error = action.payload.error;
      state.deletingTeam = false;
    });

    builder.addCase(fetchUsersOnTeam.pending, (state) => {
      state.isLoading = true;
      state.isFetchingTeamUsers = true;
    });

    builder.addCase(fetchUsersOnTeam.fulfilled, (state, action) => {
      const { page, items } = action.payload.paging;
      state.usersOnTeam = action.payload.users || [];
      state.page = page;
      state.usersOnTeamCount = items;
      state.isLoading = false;
      state.isFetchingTeamUsers = false;
      state.initiateFetchUsersOnTeam = false;
    });

    builder.addCase(fetchUsersOnTeam.rejected, (state, action) => {
      state.error = action.payload.error;
      state.isLoading = false;
      state.isFetchingTeamUsers = false;
    });

    builder.addCase(fetchUsersNotOnTeam.pending, (state) => {
      state.isLoading = true;
    });

    builder.addCase(fetchUsersNotOnTeam.fulfilled, (state, action) => {
      const { page, items } = action.payload.paging;
      state.usersNotOnTeam = action.payload.users || [];
      state.page = page;
      state.usersNotOnTeamCount = items;
      state.isLoading = false;
    });

    builder.addCase(fetchUsersNotOnTeam.rejected, (state, action) => {
      state.error = action.payload.error;
      state.isLoading = false;
    });

    builder.addCase(bulkActionTeamUsers.pending, (state) => {
      state.isLoading = true;
    });

    builder.addCase(bulkActionTeamUsers.fulfilled, (state, action) => {
      state.usersBulkEditActionResult = action.payload.apiResult.status;
      state.isLoading = false;
      state.initiateFetchUsersOnTeam = true;
    });

    builder.addCase(bulkActionTeamUsers.rejected, (state, action) => {
      state.isLoading = false;
      state.usersBulkEditActionResult = action.payload.apiResult.status;
      state.error = action.payload.error;
    });

    builder.addCase(downloadTeams.pending, (state) => {
      state.teamsDownloadedResult = '';
      state.isLoading = true;
    });

    builder.addCase(downloadTeams.fulfilled, (state, action) => {
      state.teamsDownloadedResult = action.payload.apiResult.status;
      state.isLoading = false;
    });

    builder.addCase(downloadTeams.rejected, (state, action) => {
      state.teamsDownloadedResult = action.payload.apiResult.status;
      state.error = action.payload.error;
      state.isLoading = false;
    });

    builder.addCase(fetchBgEventTasks.pending, (state) => {
      state.isFetchingBgEvents = true;
    });

    builder.addCase(fetchBgEventTasks.fulfilled, (state, action) => {
      const { page, items } = action.payload.paging;
      state.events = action.payload.events;
      state.page = page;
      state.eventsCount = items;
      state.isFetchingBgEvents = false;
    });

    builder.addCase(fetchBgEventTasks.rejected, (state, action) => {
      state.backgroundEvents.error = action.payload.error;
      state.isFetchingBgEvents = false;
    });

    builder.addCase(teamsImport.pending, (state) => {
      state.isImportingTeams = true;
    });

    builder.addCase(teamsImport.fulfilled, (state) => {
      state.isImportingTeams = false;
    });

    builder.addCase(teamsImport.rejected, (state, action) => {
      state.importTeams.error = action.payload.error;
      state.isImportingTeams = false;
    });

    builder.addCase(resetTeamAdded, (state) => {
      state.teamAdded = {
        id: '',
        name: '',
        customerId: '',
        extraFields: {},
        fullyQualifiedName: '',
      };
    });
  },
});

export default teamsSlice;
